import { Badge, Button, IconButton, Modal, Spinner, useToast } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Info, MessagesSquare, RefreshCw, Share2, Trash2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { getMicroAppInfoOptions, getMicroAppOptions } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { CenteredScreen } from "@/components/CenteredScreen";
import { type Crumb, PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { formatRelativeTime } from "@/i18n/format";
import { MicroAppAboutBody } from "./MicroAppAboutTab";
import { useDeleteMicroApp } from "./useDeleteMicroApp";
import { useMicroAppContent } from "./useMicroAppContent";
import { injectHydrationRuntime } from "./viewer/hydrationRuntime";
import { RefreshProblems } from "./viewer/RefreshProblems";
import { useDashboardHydration } from "./viewer/useDashboardHydration";

/**
 * The micro-app viewer: the published HTML rendered in an opaque-origin
 * sandboxed iframe (`sandbox="allow-scripts"` — no allow-same-origin, so the
 * content can't reach the parent's auth or DOM). Live data is hydrated via
 * the trusted postMessage runtime (viewer/hydrationRuntime.ts +
 * useDashboardHydration), ported from the old UI.
 *
 * Opened from a space (`/spaces/:spaceUuid/apps/:uuid`, with `space`) it sits under
 * that space (`{Space} / {App}`) and shows only what using the app needs (live status,
 * refresh, About); the builder actions (edit in chat, share, delete) stay in Build.
 */
export function MicroAppViewerPage({ space }: Readonly<{ space?: Crumb }> = {}) {
  const { t } = useTranslation("microApps");
  const areas = useAreaCrumbs();
  const navigate = useNavigate();
  const { uuid = "" } = useParams();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const app = useQuery(getMicroAppOptions({ path: { app_uuid: uuid } }));
  const content = useMicroAppContent(uuid);
  const { onDelete, dialog } = useDeleteMicroApp(() => navigate("/build/apps"));
  const [aboutOpen, setAboutOpen] = useState(false);
  // The About body reads /info; only fetch it when the modal opens.
  const info = useQuery({ ...getMicroAppInfoOptions({ path: { app_uuid: uuid } }), enabled: aboutOpen });

  const html = content.data ?? "";
  const srcDoc = useMemo(() => (html ? injectHydrationRuntime(html) : ""), [html]);
  const { isLive, lastUpdated, refreshing, errorCount, problems, refresh } = useDashboardHydration({
    uuid,
    html,
    iframeRef,
  });
  const toast = useToast();
  const refreshNow = async () => {
    const found = await refresh();
    if (found.length > 0) toast.warning(t("viewer.problems.toast", { count: found.length }));
  };

  if (app.isPending || content.isPending) {
    return (
      <CenteredScreen>
        <Spinner size="lg" />
      </CenteredScreen>
    );
  }
  if (app.isError || content.isError) {
    return (
      <CenteredScreen>
        <Text tone="error" role="alert">
          {t("detail.notFound")}
        </Text>
      </CenteredScreen>
    );
  }

  const data = app.data.data;
  const canEdit = data.canEdit ?? false;
  const canManage = data.canManage ?? false;
  const editInChat = canEdit && data.sourceSessionUuid ? () => navigate(`/chat/${data.sourceSessionUuid}`) : null;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <PageBreadcrumbs
        trail={
          space
            ? [space]
            : [areas.build, { label: t("title"), to: "/build/apps" }, { label: data.name, to: `/build/apps/${uuid}` }]
        }
        current={space ? data.name : t("viewer.title")}
        actions={
          <div className="flex items-center gap-2">
            {/* Live-data status + refresh (when the app binds to live data). */}
            {isLive && (
              <div className="flex items-center gap-2">
                {errorCount > 0 ? (
                  <Badge variant="warning">{t("viewer.stale")}</Badge>
                ) : (
                  <Badge variant="success">{t("viewer.live")}</Badge>
                )}
                {lastUpdated && (
                  <Text variant="caption" tone="tertiary">
                    {t("viewer.updated", { time: formatRelativeTime(lastUpdated) })}
                  </Text>
                )}
                <IconButton
                  icon={<RefreshCw className={refreshing ? "animate-spin" : undefined} />}
                  aria-label={t("viewer.refresh")}
                  variant="ghost"
                  size="sm"
                  onClick={() => void refreshNow()}
                  disabled={refreshing}
                />
              </div>
            )}

            {/* App actions, matching the old viewer's top bar. */}
            {!space && editInChat && (
              <Button variant="secondary" size="sm" leadingIcon={<MessagesSquare size={14} />} onClick={editInChat}>
                {t("viewer.editInChat")}
              </Button>
            )}
            <Button variant="secondary" size="sm" leadingIcon={<Info size={14} />} onClick={() => setAboutOpen(true)}>
              {t("viewer.about")}
            </Button>
            {!space && (
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Share2 size={14} />}
                onClick={() => navigate(`/build/apps/${uuid}/access`)}
              >
                {t("viewer.share")}
              </Button>
            )}
            {!space && canManage && (
              <IconButton
                icon={<Trash2 />}
                aria-label={t("viewer.delete")}
                variant="ghost"
                size="sm"
                onClick={() => onDelete(data)}
              />
            )}
          </div>
        }
      />
      <RefreshProblems problems={problems} />
      <iframe
        ref={iframeRef}
        srcDoc={srcDoc}
        sandbox="allow-scripts"
        title={data.name}
        className="min-h-0 w-full flex-1 border-0 bg-surface-base"
      />
      {/* About is a small read-only panel — a modal, not a trip back to the builder. */}
      <Modal open={aboutOpen} onClose={() => setAboutOpen(false)} title={t("about.titleNamed", { name: data.name })}>
        <MicroAppAboutBody info={info.data?.data} app={data} />
      </Modal>
      {dialog}
    </div>
  );
}
